import React, { useState } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DataTable } from '@/components/shared/DataTable';
import { FormDialog, FormField } from '@/components/shared/FormDialog';
import { useRoomTypes } from '@/hooks/useRoomTypes';
import { useRooms } from '@/hooks/useRooms';
import { formatCurrency } from '@/utils/format';

/**
 * Room Types Management Component
 * Manage room type definitions, pricing, and attributes
 */
export const RoomTypesManagement = ({ API }) => {
  const { roomTypes, loading, createRoomType, updateRoomType, deleteRoomType } = useRoomTypes(API);
  const { rooms } = useRooms(API);

  const [showDialog, setShowDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    num_rooms: 0,
    max_guests: 2,
    base_price: 0,
    beds24_room_type_id: null,
    non_refundable_discount: 10
  });

  // Table columns
  const columns = [
    {
      key: 'name',
      label: 'Type Name',
      sortable: true,
      render: (value) => (
        <div className="text-sm font-semibold text-gray-900">{value}</div>
      )
    },
    {
      key: 'description',
      label: 'Description',
      render: (value) => (
        <div className="text-sm text-gray-600 max-w-xs truncate">{value}</div>
      )
    },
    {
      key: 'max_guests',
      label: 'Max Guests',
      render: (value) => (
        <div className="text-sm text-gray-900">{value} guests</div>
      )
    },
    {
      key: 'base_price',
      label: 'Base Price',
      sortable: true,
      render: (value) => (
        <div className="text-sm font-medium text-gray-900">{formatCurrency(value)}</div>
      )
    },
    {
      key: 'non_refundable_discount',
      label: 'Non-Ref Discount',
      render: (value, row) => (
        <div>
          <div className="text-sm font-medium text-green-600">{value ?? 10}%</div>
          <div className="text-xs text-gray-500">
            {formatCurrency(row.base_price * (1 - (value ?? 10) / 100))}
          </div>
        </div>
      )
    },
    {
      key: 'beds24_room_type_id',
      label: 'Beds24 ID',
      render: (value) => {
        if (value) {
          return (
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
              {value}
            </span>
          );
        }
        return <span className="text-xs text-gray-400">Not mapped</span>;
      }
    },
    {
      key: 'rooms_count',
      label: 'Rooms Using',
      render: (_, roomType) => {
        const count = rooms.filter(r => r.room_type_id === roomType.id).length;
        return (
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
            {count} room{count !== 1 ? 's' : ''}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-right',
      render: (_, roomType) => {
        const roomsUsingType = rooms.filter(r => r.room_type_id === roomType.id).length;
        return (
          <div className="flex justify-end gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleEdit(roomType)}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleDelete(roomType.id)}
              disabled={roomsUsingType > 0}
              title={roomsUsingType > 0 ? 'Cannot delete: rooms are using this type' : ''}
            >
              <Trash2 className={`w-4 h-4 ${roomsUsingType > 0 ? 'text-gray-300' : 'text-red-500'}`} />
            </Button>
          </div>
        );
      }
    }
  ];

  const handleEdit = (roomType) => {
    setEditingType(roomType.id);
    setFormData({
      name: roomType.name,
      description: roomType.description,
      image_url: roomType.image_url || '',
      num_rooms: 0,
      max_guests: roomType.max_guests || 2,
      base_price: roomType.base_price || 0,
      beds24_room_type_id: roomType.beds24_room_type_id || null,
      non_refundable_discount: roomType.non_refundable_discount ?? 10
    });
    setShowDialog(true);
  };

  const handleDelete = async (roomTypeId) => {
    if (!window.confirm('Are you sure you want to delete this room type? This will fail if any rooms are using it.')) return;
    await deleteRoomType(roomTypeId);
  };

  const handleSubmit = async () => {
    const data = {
      ...formData,
      base_price: parseFloat(formData.base_price),
      max_guests: parseInt(formData.max_guests),
      beds24_room_type_id: formData.beds24_room_type_id || null,
      non_refundable_discount: formData.non_refundable_discount ?? 10
    };

    // Only include num_rooms for creation
    if (!editingType && formData.num_rooms > 0) {
      data.num_rooms = parseInt(formData.num_rooms);
    }

    if (editingType) {
      await updateRoomType(editingType, data);
    } else {
      await createRoomType(data);
    }
    
    setShowDialog(false);
    resetForm();
  };

  const resetForm = () => {
    setEditingType(null);
    setFormData({
      name: '',
      description: '',
      image_url: '',
      num_rooms: 0,
      max_guests: 2,
      base_price: 0,
      beds24_room_type_id: null,
      non_refundable_discount: 10
    });
  };

  const handleDialogClose = (open) => {
    setShowDialog(open);
    if (!open) resetForm();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Manage Room Types</h2>
          <p className="text-sm text-gray-600 mt-1">
            {roomTypes.length} room type{roomTypes.length !== 1 ? 's' : ''} total
          </p>
        </div>

        <Button 
          className="bg-gradient-to-r from-blue-600 to-indigo-600"
          onClick={() => setShowDialog(true)}
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Room Type
        </Button>
      </div>

      {/* Room Types Table */}
      <DataTable
        columns={columns}
        data={roomTypes}
        testIdPrefix="room-type"
        emptyState={
          <div className="text-center text-gray-500">
            <p>No room types added yet. Click "Add Room Type" to get started.</p>
          </div>
        }
      />

      {/* Room Type Form Dialog */}
      <FormDialog
        open={showDialog}
        onOpenChange={handleDialogClose}
        title={editingType ? 'Edit Room Type' : 'Add New Room Type'}
        onSubmit={handleSubmit}
        submitLabel={editingType ? 'Update Room Type' : 'Create Room Type'}
        size="lg"
      >
        <FormField label="Name" required htmlFor="name">
          <Input
            id="name"
            placeholder="e.g., Deluxe Room"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </FormField>

        <FormField label="Description" required htmlFor="description">
          <Textarea
            id="description"
            placeholder="Brief description of this room type"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={3}
          />
        </FormField>

        <FormField label="Image URL" htmlFor="image_url">
          <Input
            id="image_url"
            placeholder="https://example.com/image.jpg"
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <FormField label="Maximum Guests" required htmlFor="max_guests">
            <Input
              id="max_guests"
              type="number"
              min="1"
              placeholder="2"
              value={formData.max_guests}
              onChange={(e) => setFormData({ ...formData, max_guests: e.target.value })}
              required
            />
          </FormField>

          <FormField label="Base Price per Night (€)" required htmlFor="base_price">
            <Input
              id="base_price"
              type="number"
              min="0"
              step="0.01"
              placeholder="100.00"
              value={formData.base_price}
              onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
              required
            />
          </FormField>
        </div>

        {!editingType && (
          <FormField label="Quantity of This Type" htmlFor="num_rooms">
            <Input
              id="num_rooms"
              type="number"
              min="0"
              placeholder="0"
              value={formData.num_rooms}
              onChange={(e) => setFormData({ ...formData, num_rooms: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">
              Creates {formData.num_rooms || 0} rooms with auto-generated numbers
            </p>
          </FormField>
        )}

        <FormField label="Beds24 Room Type ID" htmlFor="beds24_room_type_id">
          <Input
            id="beds24_room_type_id"
            type="number"
            placeholder="Enter Beds24 room type ID"
            value={formData.beds24_room_type_id || ''}
            onChange={(e) => setFormData({ 
              ...formData, 
              beds24_room_type_id: e.target.value ? parseInt(e.target.value) : null 
            })}
          />
          <p className="text-xs text-gray-500 mt-1">
            Corresponding room type ID from your Beds24 account
          </p>
        </FormField>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <FormField label="Non-Refundable Discount (%)" htmlFor="non_refundable_discount">
            <Input
              id="non_refundable_discount"
              type="number"
              min="0"
              max="100"
              step="1"
              placeholder="10"
              value={formData.non_refundable_discount ?? 10}
              onChange={(e) => setFormData({ 
                ...formData, 
                non_refundable_discount: e.target.value === '' ? 0 : parseFloat(e.target.value) 
              })}
            />
            <p className="text-xs text-blue-700 mt-2">
              💰 {formData.non_refundable_discount === 0 
                ? '⚠️ Set to 0% - Non-refundable option will be HIDDEN from guests'
                : `Discount applied to non-refundable reservations. Default: 10%`
              }
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {formData.non_refundable_discount === 0 
                ? 'Guests will only see "Flexible Rate" and "Pay on Arrival" options.'
                : `Example: €${formData.base_price || 150}/night → €${((formData.base_price || 150) * (1 - (formData.non_refundable_discount || 10) / 100)).toFixed(2)}/night (Save €${((formData.base_price || 150) * (formData.non_refundable_discount || 10) / 100).toFixed(2)})`
              }
            </p>
          </FormField>
        </div>
      </FormDialog>
    </div>
  );
};